import communicationSdk from '@/utils/partnerSdk/communicationSdk';

// ActionLinks - turns an "/app/action/openModal/<ModalName>?..." link clicked inside the
// AI chat into an openModal call, through the same SDK utility partner apps use.
//
// Only the modals documented at https://developers.intandem.tech/docs/openmodal are
// accepted, and each one declares the params it takes. Chat content is generated text that
// may echo untrusted input, so a link can only pick a documented modal and fill declared
// params - it can never reach an undocumented modal or shape a payload of its own.

export const ACTION_LINK_PREFIX = '/app/action/openModal/';

const UID = /^[a-zA-Z0-9_-]{1,64}$/;
const TEXT = /^[^<>]{1,280}$/;
const CODE_NAME = /^[a-zA-Z0-9_-]{1,64}$/;

// Each modal declares the link params it accepts, and how they become modalProps.
// IframeModal is documented but deliberately left out: it renders an arbitrary URL, which
// is not something chat-generated content should be able to choose.
const MODALS = {
  // NewAppointment's object form is only honoured when a client object is supplied, which
  // the chat does not have - so it is passed positionally:
  // [client, conversation_uid, presetTimes, staffUid, notes, service_uid]
  NewAppointment: {
    params: {
      matter_uid: UID,
      staff_uid: UID,
      service_uid: UID,
      notes: TEXT,
    },
    toProps: (p) => [
      null,
      p.matter_uid || null,
      null,
      p.staff_uid || null,
      p.notes || null,
      p.service_uid || null,
    ],
  },
  // NewEstimate loads the matter's client itself, so the matter is enough. client_uid is
  // still accepted, because links that already carry it must keep opening the dialog.
  NewEstimate: {
    params: { matter_uid: UID, client_uid: UID },
    toProps: (p) => [null, p.matter_uid || null],
  },
  OnlineScheduling: {
    params: { staff_uid: UID },
    toProps: (p) => [{ staffUid: p.staff_uid }],
  },
  ImportContacts: {
    params: { staff_uid: UID },
    toProps: (p) => [{ staffUid: p.staff_uid }],
  },
  'calendar-sync-dialog': {
    params: { staff_uid: UID },
    toProps: (p) => [{ staffUid: p.staff_uid }],
  },
  AppMarket: {
    params: { app_code_name: CODE_NAME },
    toProps: (p) => (p.app_code_name ? [p.app_code_name] : undefined),
  },
  ViewNote: {
    params: { note_uid: UID, matter_uid: UID, client_uid: UID },
    required: ['note_uid', 'matter_uid', 'client_uid'],
    toProps: (p) => [{
      noteUid: p.note_uid,
      matterUid: p.matter_uid,
      clientUid: p.client_uid,
      enableNavigation: false,
      placement: 'ai_chat',
    }],
  },
  ClientPicker: { params: {}, toProps: () => undefined },
  'import-products-modal': { params: {}, toProps: () => undefined },
};

export const isActionLink = (href) => !!href && href.startsWith(ACTION_LINK_PREFIX);

/**
 * Resolves an action link to an openModal call.
 * Returns null for an unsupported modal, an undeclared param, or an invalid value, so the
 * caller falls back to ordinary navigation.
 *
 * @param {string} href - e.g. "/app/action/openModal/NewAppointment?matter_uid=mt_9x8y"
 * @returns {{ modalName: string, modalProps: Array|undefined }|null}
 */
export const parseActionLink = (href) => {
  if (!isActionLink(href)) return null;

  const [path, queryString] = href.split('#')[0].split('?');
  let modalName;
  try {
    modalName = decodeURIComponent(path.slice(ACTION_LINK_PREFIX.length)).replace(/\/+$/, '');
  } catch {
    return null;
  }
  if (!Object.prototype.hasOwnProperty.call(MODALS, modalName)) return null;
  const modal = MODALS[modalName];

  const params = {};
  const entries = Array.from(new URLSearchParams(queryString || '').entries());
  for (let i = 0; i < entries.length; i += 1) {
    const [key, value] = entries[i];
    const pattern = modal.params[key];
    if (!pattern || !pattern.test(value)) return null;
    params[key] = value;
  }
  if ((modal.required || []).some((key) => !params[key])) return null;

  return { modalName, modalProps: modal.toProps(params) };
};

/**
 * Opens the modal an action link stands for, via the openModal SDK utility.
 *
 * @param {string} href - the clicked link
 * @returns {boolean} true when the link opens a modal, false when it is not a valid action link
 */
export const dispatchActionLink = (href) => {
  const parsed = parseActionLink(href);
  if (!parsed) return false;

  communicationSdk('OpenModal', parsed);
  return true;
};

export default dispatchActionLink;
